/**
 * Cumple Sprint 13 — Cliente API para findings.
 */
import { apiRequest } from './client.js';

export type FindingStatus = 'open' | 'in_progress' | 'resolved' | 'closed' | 'dismissed';
export type FindingSeverity = 'low' | 'medium' | 'high' | 'critical';
export type FindingClassification = 'nc_major' | 'nc_minor' | 'observation' | 'ofi';

export interface FindingSummary {
  id: string;
  assessment_id: string;
  survey_instance_id: string;
  question_ref: string;
  question_text: string;
  response_value: string;
  /** S15 · Snapshot inmutable de la nota del auditor al detectar. */
  auditor_note: string | null;
  severity: FindingSeverity;
  classification: FindingClassification;
  status: FindingStatus;
  assigned_to_user_id: string | null;
  due_date: string | null;
  resolution: string | null;
  created_at: string;
  updated_at: string;
  closed_at: string | null;
  closed_by_user_id: string | null;
  /** Sprint Y #2 — campos enriquecidos del endpoint GET /findings */
  assigned_auditor: { id: string; email: string } | null;
  client: { id: string; name: string } | null;
  norma: { template_code: string; label: string; category: string } | null;
}

export type ViewMode = 'severity' | 'organization';

export interface FindingEvidenceRecord {
  id: string;
  finding_id: string;
  uploaded_by_user_id: string;
  content_type: string;
  size_bytes: number;
  sha256: string;
  caption: string | null;
  uploaded_at: string;
  file_url: string;
}

export interface ListFindingsResponse {
  total: number;
  items: FindingSummary[];
}

export interface ListFindingsFilters {
  status?: FindingStatus[];
  severity?: FindingSeverity[];
  classification?: FindingClassification[];
  assessment_id?: string;
  assigned_to_me?: boolean;
  /** Sprint Y #2 */
  client_id?: string;
  auditor_id?: string;
  template_category?: string;
  limit?: number;
  offset?: number;
}

export interface PatchFindingInput {
  severity?: FindingSeverity;
  classification?: FindingClassification;
  status?: FindingStatus;
  assigned_to_user_id?: string | null;
  due_date?: string | null;
  resolution?: string | null;
}

export interface FindingEventRecord {
  id: string;
  finding_id: string;
  actor_user_id: string | null;
  kind:
    | 'created'
    | 'status_changed'
    | 'severity_changed'
    | 'assigned'
    | 'due_date_changed'
    | 'resolution_updated'
    | 'evidence_uploaded'
    | 'evidence_deleted'
    | 'note_snapshot_at_capture'
    | 'classification_changed';
  from_value: string | null;
  to_value: string | null;
  metadata: unknown;
  ts: string;
}

export interface FindingPhotoRecord {
  id: string;
  question_ref: string;
  content_type: string;
  size_bytes: number;
  captured_at: string | null;
  uploaded_at: string;
  /** S16-B · Códigos de normas adicionales a las que sirve como evidencia. */
  applicable_standards: string[];
  file_url: string;
}

export const findingsApi = {
  events: (id: string) =>
    apiRequest<{ events: FindingEventRecord[] }>(`/api/v1/findings/${id}/events`),
  photos: (id: string, options: { includeWholeInstance?: boolean } = {}) =>
    apiRequest<{ photos: FindingPhotoRecord[] }>(
      `/api/v1/findings/${id}/photos${options.includeWholeInstance ? '?include=instance' : ''}`,
    ),
  list: (filters: ListFindingsFilters = {}) => {
    const qs = new URLSearchParams();
    if (filters.status?.length) qs.set('status', filters.status.join(','));
    if (filters.severity?.length) qs.set('severity', filters.severity.join(','));
    if (filters.classification?.length) qs.set('classification', filters.classification.join(','));
    if (filters.assessment_id) qs.set('assessment_id', filters.assessment_id);
    if (filters.assigned_to_me) qs.set('assigned_to_me', 'true');
    if (filters.client_id) qs.set('client_id', filters.client_id);
    if (filters.auditor_id) qs.set('auditor_id', filters.auditor_id);
    if (filters.template_category) qs.set('template_category', filters.template_category);
    if (filters.limit !== undefined) qs.set('limit', String(filters.limit));
    if (filters.offset !== undefined) qs.set('offset', String(filters.offset));
    const path = `/api/v1/findings${qs.toString() ? '?' + qs.toString() : ''}`;
    return apiRequest<ListFindingsResponse>(path);
  },
  get: (id: string) => apiRequest<{ finding: FindingSummary }>(`/api/v1/findings/${id}`),
  patch: (id: string, input: PatchFindingInput) =>
    apiRequest<{ finding: FindingSummary }>(`/api/v1/findings/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(input),
    }),
  listEvidence: (id: string) =>
    apiRequest<{ evidence: FindingEvidenceRecord[] }>(`/api/v1/findings/${id}/evidence`),
  uploadEvidence: async (
    id: string,
    file: File,
    caption?: string,
  ): Promise<FindingEvidenceRecord> => {
    const fd = new FormData();
    fd.append('file', file);
    if (caption) fd.append('caption', caption);
    const session = sessionStorage.getItem('assessos.auth.session');
    const token = session ? (JSON.parse(session) as { token: string }).token : '';
    const init: RequestInit = { method: 'POST', body: fd };
    if (token) init.headers = { Authorization: `Bearer ${token}` };
    const res = await fetch(`/api/v1/findings/${id}/evidence`, init);
    if (!res.ok) {
      const body = (await res.json().catch(() => ({}))) as { error?: string };
      throw new Error(body.error ?? `HTTP ${String(res.status)}`);
    }
    const json = (await res.json()) as { evidence: FindingEvidenceRecord };
    return json.evidence;
  },
  deleteEvidence: (evidenceId: string) =>
    apiRequest<void>(`/api/v1/findings/evidence/${evidenceId}`, { method: 'DELETE' }),
};
